import { useForm, Controller } from 'react-hook-form'
import Modal from '@components/Modal'
import Input from '@components/inputs/Input'
import Button, { LinkButton } from '@components/Button'
import { yupResolver } from '@hookform/resolvers/yup'
import useUserGovTokenAccountQuery from '@hooks/useUserGovTokenAccount'
import { BigNumber } from 'bignumber.js'
import { getMintMinAmountAsDecimal } from '@tools/sdk/units'
import { DepositInputValues } from '../types'
import {
  MAX_DECIMALS_FALLBACK,
  MAX_DEPOSIT_FALLBACK,
  MIN_DEPOSIT_FALLBACK,
  ROLE,
} from '../constants'
import { getSchema } from '../schema'
import { useDepositInDao } from './useDepositInDao'
import { useInputHandlers } from '../useInputHandlers'

type DepositInDaoModalProps = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const DepositInDaoModal = ({ isOpen, setIsOpen }: DepositInDaoModalProps) => {
  const { depositTokens, depositMintInfo, isLoading } = useDepositInDao(
    setIsOpen
  )

  const mintMaxDecimals = depositMintInfo?.decimals ?? MAX_DECIMALS_FALLBACK
  const mintMinAmount = depositMintInfo
    ? getMintMinAmountAsDecimal(depositMintInfo)
    : MIN_DEPOSIT_FALLBACK

  const userAta = useUserGovTokenAccountQuery(ROLE).data?.result
  const depositAmount = new BigNumber(
    userAta?.amount ? userAta.amount.toString() : 0
  )

  const humanReadableMax =
    depositMintInfo === undefined
      ? undefined
      : depositAmount.shiftedBy(-depositMintInfo.decimals).toNumber()
  const inputPlaceholder = humanReadableMax?.toString() + ' (max)'
  const mintMaxAmount = humanReadableMax ?? MAX_DEPOSIT_FALLBACK

  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<DepositInputValues>({
    resolver: yupResolver(
      getSchema(mintMinAmount, mintMaxAmount, mintMaxDecimals)
    ),
    mode: 'onChange',
  })

  const isDepositDisabled = Object.keys(errors).length > 0 || isLoading

  const setMax = () => {
    if (!humanReadableMax) return

    setValue('amount', humanReadableMax, {
      shouldValidate: true,
    })
  }

  const { handleOnBlur, handleOnChange } = useInputHandlers({
    mintMinAmount,
    mintMaxAmount,
    mintMaxDecimals,
    setValue,
  })

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <h2 className="mb-4">Deposit Tokens</h2>

      <form
        className="flex flex-col gap-y-4"
        onSubmit={handleSubmit(depositTokens)}
      >
        <div className="flex justify-between">
          <label>Amount to deposit</label>

          <LinkButton
            className="text-primary-light"
            disabled={!humanReadableMax}
            onClick={setMax}
          >
            Max: {humanReadableMax}
          </LinkButton>
        </div>

        <Controller
          name="amount"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="number"
              autoComplete="off"
              max={mintMaxAmount}
              min={mintMinAmount}
              step={mintMinAmount}
              placeholder={inputPlaceholder}
              error={errors.amount?.message}
              onBlur={handleOnBlur}
              onChange={handleOnChange}
            />
          )}
        />

        <Button
          type="submit"
          isLoading={isLoading}
          disabled={isDepositDisabled}
        >
          Confirm
        </Button>
      </form>
    </Modal>
  )
}

export default DepositInDaoModal
